import { infiniteQueryOptions, queryOptions, type QueryKey } from "react-query";

import { searchFilterOptions } from "@/react/components/FilterKit/inputs/AsyncSearchFilterInput/searchFilterOptions";
import type { AsyncSearchResponse } from "@/react/components/FilterKit/inputs/AsyncSearchFilterInput/searchFilterOptions";
import { reactQueryGet } from "@/react/helpers/backendRequestHelpers";
import { createQueryKeyFactory } from "@/react/lib/queries/createQueryKeyFactory";
import { queryClient } from "@/react/providers/QueryClient/reactQueryConfig";

const SEARCH_DELAY_MS = 300;
const STALE_TIME_MS = 60_000;
const CACHE_TIME_MS = 5 * 60_000;

export const filterKitOptionKey = createQueryKeyFactory("filter-kit-option");

interface SearchParams {
  filterKey: string;
  query: string;
  url: string;
}

interface SelectedParams {
  filterKey: string;
  selectedValue: string;
  url: string;
}

export const filterKitOptionQueries = {
  search: ({ filterKey, query, url }: SearchParams) =>
    infiniteQueryOptions({
      queryKey: filterKitOptionKey.key(filterKey, "search", url, query),
      initialPageParam: 1,
      queryFn: async ({
        pageParam = 1,
        signal,
      }: {
        pageParam?: number;
        signal?: AbortSignal;
      }) => {
        // Opening an empty inline picker and paging do not wait for typing.
        if (pageParam === 1 && query.trim()) {
          await new Promise(resolve => setTimeout(resolve, SEARCH_DELAY_MS));
        }
        signal?.throwIfAborted();

        return searchFilterOptions({
          url,
          query,
          signal,
          cursor: pageParam,
          fetchJson: reactQueryGet<AsyncSearchResponse>,
        });
      },
      getNextPageParam: lastPage => lastPage.nextCursor,
      gcTime: CACHE_TIME_MS,
      staleTime: STALE_TIME_MS,
      retry: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    }),
  selected: ({ filterKey, selectedValue, url }: SelectedParams) =>
    queryOptions({
      queryKey: filterKitOptionKey.key(
        filterKey,
        "selected",
        url,
        selectedValue,
      ),
      queryFn: async ({ signal }: { signal?: AbortSignal }) => {
        const { items } = await searchFilterOptions({
          url,
          query: "",
          values: [selectedValue],
          signal,
          cursor: 1,
          fetchJson: reactQueryGet<AsyncSearchResponse>,
        });

        return items.find(option => option.value === selectedValue) ?? null;
      },
      gcTime: CACHE_TIME_MS,
      staleTime: STALE_TIME_MS,
      retry: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    }),
};

const invalidateOptions = async (queryKey: QueryKey) => {
  await queryClient.cancelQueries({ queryKey });
  return queryClient.invalidateQueries({ queryKey });
};

export const filterKitOptionInvalidations = {
  all: () => invalidateOptions(filterKitOptionKey.root()),
  forFilter: (filterKey: string) =>
    invalidateOptions(filterKitOptionKey.key(filterKey)),
};
